import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import Navbar      from './components/Navbar/Navbar';
import Login       from './pages/Login/Login';
import Dashboard   from './pages/Dashboard/Dashboard';
import Orders      from './pages/Orders/Orders';
import Analytics   from './pages/Analytics/Analytics';
import Strategy    from './pages/Strategy/Strategy';
import Admin       from './pages/Admin/Admin';
import WelcomePage from './pages/Welcome/WelcomePage';

/* ── Full screen loader ── */
function AppLoader() {
  return (
    <div style={{
      minHeight:      '100vh',
      background:     '#060912',
      display:        'flex',
      flexDirection:  'column',
      alignItems:     'center',
      justifyContent: 'center',
      gap:            '16px',
    }}>
      <div style={{
        width:        '40px',
        height:       '40px',
        borderRadius: '50%',
        border:       '3px solid #1e2d45',
        borderTop:    '3px solid #00e5a0',
        animation:    'spin 0.8s linear infinite',
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <p style={{ color: '#5a6a82', fontSize: '13px', fontFamily: "'DM Mono', monospace" }}>
        Verifying session…
      </p>
    </div>
  );
}

/* ── Layout with shared Navbar ── */
function AppLayout({ children }) {
  return (
    <div style={{ position: 'relative', zIndex: 1 }}>
      <Navbar />
      {children}
    </div>
  );
}

/* ── Onboarding guard ──
   Redirects users with no brokers to /welcome.
   /strategy is exempt so new users can explore
   even while waiting for admin approval.
── */
function OnboardingGuard({ children }) {
  const { user } = useAuth();
  const location = useLocation();

  const hasBrokers = (user?.brokers?.length ?? 0) > 0;

  /* strategy is always accessible — CTA from welcome page lands here */
  if (location.pathname === '/strategy') return children;

  if (!hasBrokers) {
    return <Navigate to="/welcome" replace />;
  }

  return children;
}

/* ── Protected route ──
   Checks auth, then runs OnboardingGuard inside AppLayout
── */
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <AppLoader />;
  if (!user)   return <Navigate to="/login" replace />;
  return (
    <AppLayout>
      <OnboardingGuard>
        {children}
      </OnboardingGuard>
    </AppLayout>
  );
}

/* ── Welcome route ──
   Authenticated users only. No Navbar. No onboarding guard.
   If user already has brokers → skip to dashboard.
── */
function WelcomeRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <AppLoader />;
  if (!user)   return <Navigate to="/login" replace />;
  if ((user?.brokers?.length ?? 0) > 0) return <Navigate to="/dashboard" replace />;
  return children;
}

/* ── Admin route ── */
function AdminRoute({ children }) {
  const { user, loading, isAdmin } = useAuth();
  if (loading)  return <AppLoader />;
  if (!user)    return <Navigate to="/login" replace />;
  if (!isAdmin) return <Navigate to="/dashboard" replace />;
  return children;
}

/* ── Public route — redirect if already logged in ── */
function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <AppLoader />;
  if (user)    return <Navigate to="/dashboard" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 3500,
            style: {
              background:   '#0c1120',
              color:        '#e8edf5',
              border:       '1px solid #1e2d45',
              fontFamily:   "'DM Mono', monospace",
              fontSize:     '13px',
              borderRadius: '10px',
            },
            success: { iconTheme: { primary: '#00e5a0', secondary: '#0c1120' } },
            error:   { iconTheme: { primary: '#ff4d6d', secondary: '#0c1120' } },
          }}
        />
        <Routes>
          {/* Public */}
          <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />

          {/* Welcome — auth required, no broker required, no Navbar */}
          <Route path="/welcome" element={<WelcomeRoute><WelcomePage /></WelcomeRoute>} />

          {/* Protected — OnboardingGuard inside ProtectedRoute */}
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/orders"    element={<ProtectedRoute><Orders /></ProtectedRoute>} />
          <Route path="/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
          <Route path="/strategy"  element={<ProtectedRoute><Strategy /></ProtectedRoute>} />

          {/* Admin — own layout, no shared Navbar */}
          <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}